import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "../../utils/axios";
import { Alert, Button } from "antd";
import useLang from "../../hooks/useLang";
import useCachedResource from "../../hooks/useCachedResource";
import {
	getUserMenuOptionsFromToken,
	getUserRoleFromToken,
} from "../../utils/Auth";
import "slick-carousel/slick/slick.css";
import "./Dashboard.css";
import { Link } from "react-router-dom";
import { ReactComponent as SliderRight } from "../../assets/sliderRight.svg";
import { ReactComponent as SliderLeft } from "../../assets/sliderLeft.svg";
import OrderStatus from "../../components/OrderStatus";
import {
	AreaChart,
	Area,
	Tooltip,
	CartesianGrid,
	XAxis,
	YAxis,
	ResponsiveContainer,
} from "recharts";
import { useDispatch } from "react-redux";
import { orderStatistic } from "../../redux/orderSlice";
import { userRoles } from "../../types/enums";
import Slider from "react-slick";
import Map from "./Map";
import ProgressListTable, {
	ProgressListItem,
} from "../../components/ProgressListTable";
import ProcessChart from "../../components/ProgressChart";
import MySliderToggle from "../../components/MySliderToggle";
import classes from "../../components/FilterOrderSection.module.css";
import DefaultSelect from "../../components/form/DefaultSelect";
import DefaultDatePicker from "../../components/form/DefaultDatePicker";
import { orderStatusColor } from "../../lib/orderStatusColors";
import OrderStatusIcon from "../../components/OrderStatusIcon";
import {
	ChartSkeleton,
	LatestOrdersSkeleton,
	ListSkeleton,
	StatusCardsSkeleton,
	TopProgressBar,
	useLoadingMessage,
} from "./DashboardSkeletons";

/*
 * This is the first screen after login and every statistic on it is an aggregate query; the
 * status counts and the charts take tens of seconds on the database. So the page:
 *
 * - only asks for what it renders: the top-5 tables are admin-only, the returned-by-city figures
 *   load the first time someone switches the map to them, and the filters drive only the three
 *   statistics that accept them;
 * - serves every section except latest orders from the browser cache (utils/requestCache) and
 *   refreshes it in the background once it is older than its TTL, so coming back to the dashboard
 *   is instant and does not re-run the queries;
 * - renders each section as soon as its own data arrives instead of waiting for the slowest one.
 *
 * Requests are `silent`: each section shows its own spinner and error instead of the full-screen
 * loader and an error toast.
 */

interface ITop5ClientsForPaymentsDto {
	sellerId: number;
	nameAndSurname: string;
	paymentAmount: number;
	paymentPercentage: number;
}

interface IOrderCountDto {
	statusId: number;
	numberOfOrders: number;
}

interface IOrderDataChart {
	orderMonth: string;
	deliveredOrders: number;
	rejectedOrders: number;
}

interface Zone {
	id: number;
	description: string;
	levelId: number;
	parentId: number;
}

interface OrderCountByCity {
	city: string;
	orderCount: number;
	countryId: number;
	percentage: string;
}

interface IDailyOrder {
	id: number;
	orderId: number;
	statusId: number;
	displayId: string;
	orderDescription: string;
	statusChangeComment: string;
	fullName: string;
	phone: string;
	statusTime: string;
	statusDate: string;
}

export interface IProcessData {
	date: string;
	inTransport: number;
	inStorage: number;
	pending: number;
}

interface IOrderProcessStatisticsDto {
	theDate: string;
	inStorageCount: number;
	transportingCount: number;
	pendingCount: number;
}

interface IFilterParams {
	fromDate: string | null;
	toDate: string | null;
	countryId: number | null;
}

type CityToggle = "dorëzuar" | "kthyer";

interface ICityStatistics {
	mapData: OrderCountByCity[];
	listItems: ProgressListItem[];
}

const MINUTE = 60 * 1000;

// How long a cached section is shown without asking the API again.
// None of these need to be live; the refresh button fetches everything on demand.
const CACHE_TTL = {
	statistics: 30 * MINUTE,
	cities: 30 * MINUTE,
	topClients: 30 * MINUTE,
	reference: 60 * MINUTE,
};

const emptyParams: IFilterParams = {
	fromDate: null,
	toDate: null,
	countryId: null,
};

const cityEndpoints: Record<CityToggle, string> = {
	dorëzuar: "Dashboard/select-delivered-orders-by-city",
	kthyer: "Dashboard/select-returned-orders-by-city",
};

const LATEST_ORDERS_AUTOPLAY_MS = 4000;

// The picker hands back a dayjs value; format it in local time so a date picked shortly after
// midnight does not become the previous day the way toISOString() would.
const toApiDate = (date: any): string | null => {
	if (!date) return null;
	if (typeof date.format === "function") return date.format("YYYY-MM-DD");
	return date.toISOString().split("T")[0];
};

const getJson = async <T,>(endpoint: string, params?: object): Promise<T> => {
	const response = await api.get<T>(endpoint, { params, silent: true });
	return response.data;
};

const toProcessData = (rows: IOrderProcessStatisticsDto[]): IProcessData[] =>
	rows.map((dto) => ({
		date: new Date(dto.theDate).toLocaleDateString("en-GB", {
			day: "2-digit",
			month: "short",
			year: "numeric",
		}),
		inTransport: dto.transportingCount,
		inStorage: dto.inStorageCount,
		pending: dto.pendingCount,
	}));

// DailyOrderList_sp returns rows in no particular order; show the newest first.
const newestFirst = (orders: IDailyOrder[]): IDailyOrder[] => {
	const statusMoment = (order: IDailyOrder) =>
		`${order.statusDate} ${order.statusTime}`;
	return [...(orders ?? [])].sort((a, b) =>
		statusMoment(b).localeCompare(statusMoment(a))
	);
};

const toCityStatistics = (rows: OrderCountByCity[]): ICityStatistics => {
	const result = rows ?? [];
	const totalOrders = result.reduce((sum, city) => sum + city.orderCount, 0);
	const mapData = result
		.map((city) => ({
			...city,
			percentage:
				totalOrders > 0
					? ((city.orderCount / totalOrders) * 100).toFixed(2)
					: "0",
		}))
		.sort((a, b) => b.orderCount - a.orderCount);
	const listItems = mapData.map((city) => ({
		label: city.city,
		value: city.orderCount,
		percentage: city.percentage as unknown as number,
	}));
	return { mapData, listItems };
};

const toTop5Items = (rows: ITop5ClientsForPaymentsDto[]): ProgressListItem[] =>
	(rows ?? []).map((item) => ({
		label: item.nameAndSurname,
		value: item.paymentAmount,
		percentage: item.paymentPercentage,
	}));

// "2026-10-03T00:00:00" -> "03.10.2026"
const formatOrderDate = (date: string) => {
	const parsed = new Date(date);
	return Number.isNaN(parsed.getTime())
		? date
		: parsed
				.toLocaleDateString("en-GB", {
					day: "2-digit",
					month: "2-digit",
					year: "numeric",
				})
				.replace(/\//g, ".");
};

const formatTime = (timestamp: number) =>
	new Date(timestamp).toLocaleTimeString([], {
		hour: "2-digit",
		minute: "2-digit",
	});

const RefreshIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M21 12a9 9 0 1 1-2.64-6.36" />
		<path d="M21 3v6h-6" />
	</svg>
);

const getSlidesToShow = () => {
	if (window.innerWidth >= 1200) return 3;
	if (window.innerWidth >= 768) return 2;
	return 1;
};

const useSlidesToShow = () => {
	const [slidesToShow, setSlidesToShow] = useState(getSlidesToShow);

	useEffect(() => {
		const onResize = () => setSlidesToShow(getSlidesToShow());
		window.addEventListener("resize", onResize);
		return () => window.removeEventListener("resize", onResize);
	}, []);

	return slidesToShow;
};

/*
 * Latest orders are deliberately not cached: they must be current, and the query is cheap
 * (0.2–0.5 s, today's rows only, TOP 20), so every visit to the dashboard fetches them again.
 * Only clients and subclients see them; for staff the request is never sent.
 */
const useLatestOrders = (enabled: boolean) => {
	const [data, setData] = useState<IDailyOrder[]>();
	const [loading, setLoading] = useState(enabled);
	const [error, setError] = useState(false);
	const mounted = useRef(true);

	const reload = useCallback(async () => {
		setLoading(true);
		setError(false);
		try {
			const orders = await getJson<IDailyOrder[]>("Order/dailyOrderList");
			if (mounted.current) setData(newestFirst(orders));
		} catch {
			if (mounted.current) setError(true);
		} finally {
			if (mounted.current) setLoading(false);
		}
	}, []);

	useEffect(() => {
		mounted.current = true;
		if (enabled) reload();
		return () => {
			mounted.current = false;
		};
	}, [enabled, reload]);

	return { data, loading, error, reload };
};

const Dashboard = () => {
	const dispatch = useDispatch();
	const { strings } = useLang("dashboard");

	const [isClient] = useState(() => {
		const userRoleId = getUserRoleFromToken();
		return (
			userRoleId === userRoles.CLIENT ||
			userRoleId === userRoles.SUBCLIENT
		);
	});

	// The empty state offers "register an order" only to users who can.
	const [canRegisterOrders] = useState(
		() =>
			getUserMenuOptionsFromToken()?.includes(
				"REGISTER_ORDER_ACCESS" as any
			) ?? false
	);

	// Filter inputs, and the filter the statistics are currently showing.
	const [selectedCountry, setSelectedCountry] = useState<number | null>(null);
	const [startDate, setStartDate] = useState(null);
	const [endDate, setEndDate] = useState(null);
	const [appliedParams, setAppliedParams] =
		useState<IFilterParams>(emptyParams);
	const paramsKey = JSON.stringify(appliedParams);

	const [selectedToggle, setSelectedToggle] =
		useState<CityToggle>("dorëzuar");
	const [returnedRequested, setReturnedRequested] = useState(false);
	// undefined = follow the deployment's country; null = cleared by the user.
	const [mapCountryId, setMapCountryId] = useState<number | null>();

	const statusCounts = useCachedResource(
		`dashboard:status-counts:${paramsKey}`,
		() =>
			getJson<IOrderCountDto[]>(
				"Dashboard/order-counts-per-status",
				appliedParams
			),
		CACHE_TTL.statistics
	);
	const monthChart = useCachedResource(
		`dashboard:by-month:${paramsKey}`,
		() =>
			getJson<IOrderDataChart[]>(
				"Dashboard/order-count-by-month",
				appliedParams
			),
		CACHE_TTL.statistics
	);
	const inProcess = useCachedResource(
		`dashboard:in-process:${paramsKey}`,
		() =>
			getJson<IOrderProcessStatisticsDto[]>(
				"Dashboard/select-orders-in-process",
				appliedParams
			).then(toProcessData),
		CACHE_TTL.statistics
	);
	const latestOrders = useLatestOrders(isClient);
	const deliveredByCity = useCachedResource(
		"dashboard:cities:delivered",
		() =>
			getJson<OrderCountByCity[]>(cityEndpoints.dorëzuar).then(
				toCityStatistics
			),
		CACHE_TTL.cities
	);
	const returnedByCity = useCachedResource(
		returnedRequested ? "dashboard:cities:returned" : null,
		() =>
			getJson<OrderCountByCity[]>(cityEndpoints.kthyer).then(
				toCityStatistics
			),
		CACHE_TTL.cities
	);
	const topTransportClients = useCachedResource(
		isClient ? null : "dashboard:top5:transport",
		() =>
			getJson<ITop5ClientsForPaymentsDto[]>(
				"Dashboard/select-top5-clients-for-transport"
			).then(toTop5Items),
		CACHE_TTL.topClients
	);
	const topOrderClients = useCachedResource(
		isClient ? null : "dashboard:top5:orders",
		() =>
			getJson<ITop5ClientsForPaymentsDto[]>(
				"Dashboard/select-top5-clients-for-orders"
			).then(toTop5Items),
		CACHE_TTL.topClients
	);
	const zones = useCachedResource(
		"reference:zones",
		() => getJson<Zone[]>("Zone/zones"),
		CACHE_TTL.reference
	);
	const deploymentCountry = useCachedResource(
		"reference:country-id",
		() => getJson<number>("Zone/country-id"),
		CACHE_TTL.reference
	);

	// OrderStatus reads the counts from redux. Clear them while the current filter's counts are
	// missing (loading or failed), so the cards never show another filter's numbers.
	useEffect(() => {
		dispatch(orderStatistic(statusCounts.data ?? []));
	}, [dispatch, statusCounts.data]);

	const statistics = [statusCounts, monthChart, inProcess];
	const statisticsLoading = statistics.some((s) => s.loading);
	const failedStatistics = statistics.filter((s) => s.error && !s.data);
	// Shown once all three statistics are on screen: the oldest of them.
	const lastUpdated = statistics.every((s) => s.updatedAt !== undefined)
		? Math.min(...statistics.map((s) => s.updatedAt as number))
		: undefined;

	const cityStatistics =
		selectedToggle === "dorëzuar" ? deliveredByCity : returnedByCity;
	const mapCountryValue =
		mapCountryId === undefined
			? deploymentCountry.data ?? null
			: mapCountryId;

	const dailyOrderList = latestOrders.data ?? [];
	const sliderRef = useRef<Slider | null>(null);
	const slidesToShow = useSlidesToShow();
	const canSlide = dailyOrderList.length > slidesToShow;
	const sliderSettings = {
		dots: false,
		arrows: false,
		speed: 500,
		slidesToShow,
		slidesToScroll: 1,
		infinite: canSlide,
		autoplay: canSlide,
		autoplaySpeed: LATEST_ORDERS_AUTOPLAY_MS,
		pauseOnHover: true,
		pauseOnFocus: true,
	};

	const countryOptions = (zones.data ?? [])
		.filter((country: Zone) => country.levelId === 1)
		.map((country: Zone) => ({
			value: country.id,
			label: country.description,
		}));

	const onCityToggleChange = (value: CityToggle) => {
		setSelectedToggle(value);
		if (value === "kthyer") setReturnedRequested(true);
	};

	const onFilterCountrySelected = (value: any) => {
		if (!value) {
			setSelectedCountry(null);
		} else {
			setSelectedCountry(value);
			setMapCountryId(value);
		}
	};

	// Applying the filter that is already showing changes no cache key, so it sends nothing.
	const handleFilterSubmit = () =>
		setAppliedParams({
			fromDate: toApiDate(startDate),
			toDate: toApiDate(endDate),
			countryId: selectedCountry || null,
		});

	const handleResetFilters = () => {
		setSelectedCountry(null);
		setStartDate(null);
		setEndDate(null);
		setAppliedParams(emptyParams);
	};

	const handleRefresh = () => {
		const sections = [
			...statistics,
			...(isClient ? [latestOrders] : []),
			cityStatistics,
			...(isClient ? [] : [topTransportClients, topOrderClients]),
		];
		sections.forEach((section) => section.reload());
	};

	const statusPending = statusCounts.loading && !statusCounts.data;
	const monthPending = monthChart.loading && !monthChart.data;
	const inProcessPending = inProcess.loading && !inProcess.data;
	const loadingMessage = useLoadingMessage(
		statusPending || monthPending || inProcessPending,
		[strings?.loadingStep1, strings?.loadingStep2, strings?.loadingStep3]
	);
	const pageBusy =
		statisticsLoading ||
		cityStatistics.loading ||
		topTransportClients.loading ||
		topOrderClients.loading ||
		(isClient && latestOrders.loading);

	return (
		<div className="dashboardContainer">
			<TopProgressBar active={pageBusy} />
			{isClient && (
				<section className="dashboardRow latestOrdersCard">
					<div className="latestOrdersHeader">
						<div className="latestOrdersHeading">
							<h2 className="StatisticTitle">
								{strings?.latestOrders}
							</h2>
							{dailyOrderList.length > 0 && (
								<span className="latestOrdersCount">
									{strings?.today} · {dailyOrderList.length}
								</span>
							)}
						</div>
						{canSlide && (
							<div className="buttonsSlider">
								<button
									type="button"
									className="sliderArrow"
									aria-label="Previous"
									onClick={() =>
										sliderRef.current?.slickPrev()
									}
								>
									<SliderLeft />
								</button>
								<button
									type="button"
									className="sliderArrow"
									aria-label="Next"
									onClick={() =>
										sliderRef.current?.slickNext()
									}
								>
									<SliderRight />
								</button>
							</div>
						)}
					</div>
					{dailyOrderList.length > 0 ? (
						<Slider
							{...sliderSettings}
							ref={(slider) => {
								sliderRef.current = slider;
							}}
						>
							{dailyOrderList.map((order) => (
								<div key={order.id}>
									<Link
										to={`/order-details?id=${order.orderId}`}
										className="latestOrderItem"
										style={
											{
												"--status-color":
													orderStatusColor(
														order.statusId
													),
											} as React.CSSProperties
										}
										title={strings?.orderDetails}
									>
										<span className="latestOrderIcon">
											<OrderStatusIcon
												statusId={order.statusId}
											/>
										</span>
										<div className="latestOrderBody">
											<div className="latestOrderTop">
												<span className="latestOrderNumber">
													#{order.displayId}
												</span>
												<span
													className="latestOrderArrow"
													aria-hidden="true"
												>
													<svg
														width="14"
														height="14"
														viewBox="0 0 24 24"
														fill="none"
														stroke="currentColor"
														strokeWidth="2.2"
														strokeLinecap="round"
														strokeLinejoin="round"
													>
														<path d="M5 12h14M13 6l6 6-6 6" />
													</svg>
												</span>
											</div>
											<span className="latestOrderStatus">
												{order.statusChangeComment}
											</span>
											<span className="latestOrderTime">
												{formatOrderDate(
													order.statusDate
												)}
												{order.statusTime &&
													` · ${order.statusTime}`}
											</span>
										</div>
									</Link>
								</div>
							))}
						</Slider>
					) : latestOrders.loading ? (
						<LatestOrdersSkeleton count={slidesToShow} />
					) : (
						<div className="latestOrdersEmpty">
							<span
								className="latestOrdersEmptyArt"
								aria-hidden="true"
							>
								<svg
									width="26"
									height="26"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="1.6"
									strokeLinecap="round"
									strokeLinejoin="round"
								>
									<path d="M21 8l-9-5-9 5v8l9 5 9-5z" />
									<path d="M3 8l9 5 9-5" />
									<path d="M12 13v8" />
									<path d="M7.5 5.5l9 5" />
								</svg>
							</span>
							<div className="latestOrdersEmptyText">
								<p className="latestOrdersEmptyTitle">
									{latestOrders.error
										? strings?.loadError
										: strings?.noOrdersToday}
								</p>
								{!latestOrders.error && (
									<p className="latestOrdersEmptyHint">
										{strings?.noOrdersTodayHint}
									</p>
								)}
							</div>
							{latestOrders.error ? (
								<Button
									className="latestOrdersEmptyAction"
									onClick={latestOrders.reload}
								>
									{strings?.retry}
								</Button>
							) : (
								canRegisterOrders && (
									<Link
										to="/register-order"
										className="latestOrdersEmptyAction"
									>
										+ {strings?.registerOrder}
									</Link>
								)
							)}
						</div>
					)}
				</section>
			)}

			<div className="dashboardRow dashboardWrapper">
				<div className="filtersWrapper">
					<div className="dashboardTitle">
						<div className="text-xl font-semibold">
							{strings?.mainDashboard}
						</div>
						<div className="dashboardFreshness">
							{lastUpdated !== undefined && (
								<span>
									{strings?.lastUpdated}{" "}
									{formatTime(lastUpdated)}
								</span>
							)}
							<Button
								type="text"
								size="small"
								className={`dashboardRefresh${
									statisticsLoading ? " isRefreshing" : ""
								}`}
								icon={<RefreshIcon />}
								aria-busy={statisticsLoading}
								onClick={handleRefresh}
								aria-label={strings?.refresh}
								title={strings?.refresh}
							/>
						</div>
					</div>
					<div className="filtersContainer">
						<DefaultSelect
							label={strings?.country}
							placeholder={strings?.chooseCountry}
							options={countryOptions}
							value={selectedCountry ?? undefined}
							onChange={onFilterCountrySelected}
							allowClear
							flexDirection="col"
							alignCenter="start"
							className={classes.input}
						/>
						<div className={`${classes.datat} filtersDates`}>
							<DefaultDatePicker
								label={strings?.fromdate}
								value={startDate}
								onChange={(date: any) =>
									setStartDate(date ?? null)
								}
								width={172}
								className={classes.firstData}
							/>
							<DefaultDatePicker
								label={strings?.untill}
								value={endDate}
								onChange={(date: any) =>
									setEndDate(date ?? null)
								}
								width={172}
								className={classes.secondData}
							/>
						</div>
						<div className="filtersButtons">
							<Button
								onClick={handleFilterSubmit}
								className="filterButton filterButtonPrimary"
							>
								{"Filtro"}
							</Button>
							<Button
								onClick={handleResetFilters}
								className="filterButton"
							>
								{strings?.reset}
							</Button>
						</div>
					</div>
				</div>

				{failedStatistics.length > 0 && !statisticsLoading && (
					<Alert
						type="warning"
						showIcon
						message={strings?.loadError}
						action={
							<Button
								size="small"
								onClick={() =>
									failedStatistics.forEach((s) => s.reload())
								}
							>
								{strings?.retry}
							</Button>
						}
					/>
				)}

				<div className="firstDashboardContainer">
					{statusPending ? (
						<StatusCardsSkeleton message={loadingMessage} />
					) : (
						<OrderStatus />
					)}

					<div className="statisticDashboard">
						<div className="statisticFirst">
							<p className="statisticParagraph">
								<b>{strings?.statsTitle}</b>
							</p>
							<div className="statisticSecond">
								<p>
									<span className="text-[#4F16C2]">•</span>{" "}
									{strings?.delivered}
								</p>
								<p>
									<span className="text-[#FF2828]">•</span>{" "}
									{strings?.returned}
								</p>
							</div>
						</div>
						{monthPending ? (
							<ChartSkeleton
								message={
									statusPending ? undefined : loadingMessage
								}
							/>
						) : (
							<ResponsiveContainer width="100%" height={250}>
								<AreaChart
									data={monthChart.data ?? []}
									margin={{
										top: 10,
										right: 30,
										left: 0,
										bottom: 0,
									}}
								>
									<defs>
										<linearGradient
											id="colorUv"
											x1="0"
											y1="0"
											x2="0"
											y2="1"
										>
											<stop
												offset="20%"
												stopColor="#8884d8"
												stopOpacity={0.8}
											/>
											<stop
												offset="95%"
												stopColor="#8884d8"
												stopOpacity={0}
											/>
										</linearGradient>
										<linearGradient
											id="colorRv"
											x1="0"
											y1="0"
											x2="0"
											y2="1"
										>
											<stop
												offset="20%"
												stopColor="#ff0000"
												stopOpacity={0.8}
											/>
											<stop
												offset="95%"
												stopColor="#ff0000"
												stopOpacity={0}
											/>
										</linearGradient>
									</defs>
									<XAxis dataKey="orderMonth" />
									<YAxis />
									<CartesianGrid strokeDasharray="3 3" />
									<Tooltip />
									<Area
										type="monotone"
										dataKey="deliveredOrders"
										stroke="#4F16C2"
										fillOpacity={1}
										fill="url(#colorUv)"
										name={strings?.delivered}
									/>
									<Area
										type="monotone"
										dataKey="rejectedOrders"
										stroke="#ff0000"
										fillOpacity={1}
										fill="url(#colorRv)"
										name={strings?.returned}
									/>
								</AreaChart>
							</ResponsiveContainer>
						)}
					</div>
				</div>

				<div className="secondStatisticDashboard">
					{inProcessPending ? (
						<div className="background-div chartCardSkeleton">
							<p className="statisticParagraph">
								<b>{strings?.processChart}</b>
							</p>
							<ChartSkeleton
								height={300}
								message={
									statusPending || monthPending
										? undefined
										: loadingMessage
								}
							/>
						</div>
					) : (
						<ProcessChart
							title={strings?.processChart}
							transportString={strings?.transport}
							inStorageString={strings?.inStorage}
							pendingString={strings?.pending}
							data={inProcess.data ?? []}
						/>
					)}
				</div>
			</div>

			{!isClient && (
				<div className="dashboardRow topClientsRow">
					{topTransportClients.loading &&
					!topTransportClients.data ? (
						<ListSkeleton title={strings?.topClients} />
					) : (
						<ProgressListTable
							title={strings?.topClients}
							items={topTransportClients.data ?? []}
							progressBarColor={"#4F16C2"}
							subTitle={strings?.subtitleForTranasportPayments}
						/>
					)}
					{topOrderClients.loading && !topOrderClients.data ? (
						<ListSkeleton title={strings?.topClients} />
					) : (
						<ProgressListTable
							title={strings?.topClients}
							items={topOrderClients.data ?? []}
							progressBarColor={"#28BFFF"}
							subTitle={strings?.subtitleForOrderPayments}
						/>
					)}
				</div>
			)}

			<div className="dashboardRow mapStaticsInfo">
				<div className="hartInfo">
					<div className="hartHeader">
						<div className="flex flex-col gap-4">
							<h2 className="StatisticTitle">{strings?.map}</h2>
							<DefaultSelect
								label={strings?.country}
								placeholder={strings?.chooseCountry}
								options={countryOptions}
								value={mapCountryValue ?? undefined}
								onChange={(value: any) =>
									setMapCountryId(value ?? null)
								}
								allowClear
								flexDirection="col"
								alignCenter="start"
								className={classes.input}
							/>
						</div>
						<MySliderToggle
							value={selectedToggle}
							onChange={onCityToggleChange}
							deliveredString={strings?.delivered}
							returnedString={strings?.returned}
						/>
					</div>
					<div className="hartDescTitle">
						<Map
							ordersCountByCity={
								cityStatistics.data?.mapData ?? []
							}
							countryId={mapCountryValue}
							selectedToggle={selectedToggle}
						/>
					</div>
				</div>
				{cityStatistics.loading && !cityStatistics.data ? (
					<ListSkeleton title={strings?.ordersByCity} rows={8} />
				) : (
					<ProgressListTable
						title={strings?.ordersByCity}
						items={cityStatistics.data?.listItems ?? []}
						style={{ backgroundColor: "#FBFBFB" }}
						progressBarColor={"#CFBFEE"}
					/>
				)}
			</div>
		</div>
	);
};

export default Dashboard;
